import { NextPage } from 'next';
import { Vertical } from '../../types/contentstack';
import styled from 'styled-components';
import { getAssetUrl } from '../../utils/contentstack';
import { mediaScreen } from '../../utils/mediaScreen';
import { Col, ResponsiveContainer, Row } from '../responsive';
import {
  HeroHeadline,
  HeroSubheadline,
  SectionEyebrow,
  SectionHeadline,
  SectionSubheadline,
} from '../section';
import Link from 'next/link';
import { Button } from '../ui/button';
import { Accordion } from '../accordion';
import { OptimizedImage } from '../ui/optimizedImage';

export const VerticalPage: NextPage<{ page: Vertical }> = ({ page }) => {
  return (
    <>
      <Hero>
        <div className="fade" />
        <div className="image">
          <OptimizedImage
            src={getAssetUrl(page?.hero?.imageConnection)}
            height={800}
            width={750}
          />
        </div>

        <ResponsiveContainer className="content">
          <SectionEyebrow>{page?.hero?.eyebrow}</SectionEyebrow>
          <HeroHeadline
            dangerouslySetInnerHTML={{ __html: page?.hero?.headline || '' }}
          />
          <HeroSubheadline
            dangerouslySetInnerHTML={{ __html: page?.hero?.subheadline! }}
          />

          <div className="buttons">
            {!!page?.hero?.primary_button?.title && (
              <Link
                href={page?.hero?.primary_button?.href!}
                legacyBehavior
                passHref
              >
                <Button mType="primary">
                  {page?.hero?.primary_button?.title}
                </Button>
              </Link>
            )}

            {!!page?.hero?.secondary_button?.title && (
              <Link
                href={page?.hero?.secondary_button?.href!}
                legacyBehavior
                passHref
              >
                <Button mType="secondary">
                  {page?.hero?.secondary_button?.title}
                </Button>
              </Link>
            )}
          </div>
        </ResponsiveContainer>
      </Hero>

      {!!page?.section_1?.headline && (
        <Section1>
          <ResponsiveContainer>
            <Row>
              <Col xs={12} md={5} className="image">
                <img src={getAssetUrl(page?.section_1?.imageConnection)} />
              </Col>

              <Col xs={12} md={7} className="texts">
                <SectionEyebrow>{page?.section_1?.eyebrow}</SectionEyebrow>
                <SectionHeadline>{page?.section_1?.headline}</SectionHeadline>
                <SectionSubheadline
                  dangerouslySetInnerHTML={{
                    __html: page?.section_1?.subheadline!,
                  }}
                />
              </Col>
            </Row>
          </ResponsiveContainer>
        </Section1>
      )}

      {!!page?.section_2?.headline && (
        <Section2>
          <ResponsiveContainer>
            <Row>
              <Col xs={12} md={5} className="texts">
                <SectionEyebrow invert>
                  {page?.section_2?.eyebrow}
                </SectionEyebrow>
                <SectionHeadline invert>
                  {page?.section_2?.headline}
                </SectionHeadline>
                <SectionSubheadline
                  invert
                  dangerouslySetInnerHTML={{
                    __html: page?.section_2?.subheadline!,
                  }}
                />

                {!!page?.section_2?.primary_button?.title && (
                  <Link
                    href={page?.section_2?.primary_button?.href!}
                    legacyBehavior
                    passHref
                  >
                    <Button mType="primary">
                      {page?.section_2?.primary_button?.title}
                    </Button>
                  </Link>
                )}
              </Col>

              <Col xs={12} md={7} className="accordion-wrapper">
                <Accordion
                  invert
                  items={page?.section_2?.accordion_items?.map((item) => ({
                    title: item?.title!,
                    content: item?.content!,
                  }))}
                />
              </Col>
            </Row>
          </ResponsiveContainer>
        </Section2>
      )}

      {!!page?.section_3?.headline && (
        <Section3>
          <SectionEyebrow>{page?.section_3?.eyebrow}</SectionEyebrow>
          <SectionHeadline
            dangerouslySetInnerHTML={{
              __html: page?.section_3?.headline || '',
            }}
          />

          <div className="cards">
            {page?.section_3?.cards?.map((card, idx) => {
              const icon = getAssetUrl(card?.iconConnection);

              return (
                <div className="card" key={idx}>
                  <div className="head">
                    <img
                      src={icon}
                      alt=""
                      aria-hidden="true"
                      className="icon"
                      width={60}
                      height={60}
                    />

                    <h3
                      className="title"
                      dangerouslySetInnerHTML={{ __html: card?.title! }}
                    />
                  </div>

                  <p
                    className="content"
                    dangerouslySetInnerHTML={{
                      __html: card?.content || '',
                    }}
                  />
                </div>
              );
            })}
          </div>

          <div className="buttons">
            <Link
              href={page?.section_3?.primary_button?.href || ''}
              passHref
              legacyBehavior
            >
              <Button as="a" mType="primary">
                {page?.section_3?.primary_button?.title}
              </Button>
            </Link>

            <Link
              href={page?.section_3?.secondary_button?.href || ''}
              passHref
              legacyBehavior
            >
              <Button as="a" mType="secondary">
                {page?.section_3?.secondary_button?.title}
              </Button>
            </Link>
          </div>
        </Section3>
      )}

      {!!page?.section_4?.headline && (
        <Section4>
          <img className="angle" src="/img/quotes-angle.svg" alt="" />

          <ResponsiveContainer>
            <Row>
              <Col xs={12} md={6} className="image">
                <img src={getAssetUrl(page?.section_4?.imageConnection)} />
              </Col>

              <Col xs={12} md={6} className="texts">
                <SectionEyebrow invert>
                  {page?.section_4?.eyebrow}
                </SectionEyebrow>
                <SectionHeadline invert>
                  {page?.section_4?.headline}
                </SectionHeadline>
                <SectionSubheadline
                  invert
                  dangerouslySetInnerHTML={{
                    __html: page?.section_4?.subheadline!,
                  }}
                />

                {!!page?.section_4?.primary_button?.title && (
                  <Link
                    href={page?.section_4?.primary_button?.href!}
                    legacyBehavior
                    passHref
                  >
                    <Button mType="primary">
                      {page?.section_4?.primary_button?.title}
                    </Button>
                  </Link>
                )}
              </Col>
            </Row>
          </ResponsiveContainer>
        </Section4>
      )}

      {!!page?.section_5?.headline && (
        <Section5
          columns={
            (page?.section_5?.cards?.length || 0) <= 4
              ? page?.section_5?.cards?.length!
              : 4
          }
        >
          <SectionEyebrow>{page?.section_5?.eyebrow}</SectionEyebrow>
          <SectionHeadline
            dangerouslySetInnerHTML={{
              __html: page?.section_5?.headline || '',
            }}
          />
          <SectionSubheadline
            dangerouslySetInnerHTML={{
              __html: page?.section_5?.subheadline || '',
            }}
          />

          <div className="cards">
            {page?.section_5?.cards?.map((card, idx) => {
              const icon = getAssetUrl(card?.iconConnection);

              return (
                <div className="card" key={idx}>
                  <img
                    src={icon}
                    alt=""
                    aria-hidden="true"
                    className="icon"
                    width={60}
                    height={60}
                  />

                  <h3
                    className="title"
                    dangerouslySetInnerHTML={{ __html: card?.title! }}
                  />

                  <p
                    className="content"
                    dangerouslySetInnerHTML={{
                      __html: card?.content || '',
                    }}
                  />
                </div>
              );
            })}
          </div>

          {!!page?.section_5?.supporting_text && (
            <p
              className="supporting-text"
              dangerouslySetInnerHTML={{
                __html: page?.section_5?.supporting_text || '',
              }}
            />
          )}
        </Section5>
      )}

      {!!page?.section_6?.headline && (
        <Section6
          columns={
            (page?.section_6?.grid?.items?.length || 0) <= 3
              ? page?.section_6?.grid?.items?.length!
              : 2
          }
        >
          <ResponsiveContainer>
            <Row>
              <Col xs={12} md={5} className="texts">
                <SectionEyebrow invert>
                  {page?.section_6?.eyebrow}
                </SectionEyebrow>

                <SectionHeadline invert>
                  {page?.section_6?.headline}
                </SectionHeadline>

                <SectionSubheadline
                  invert
                  dangerouslySetInnerHTML={{
                    __html: page?.section_6?.subheadline!,
                  }}
                />
              </Col>

              <Col xs={12} md={7} className="grid-wrapper">
                {!!page?.section_6?.grid?.title && (
                  <h2 className="grid-title">{page?.section_6?.grid?.title}</h2>
                )}

                <div className="grid">
                  {page?.section_6?.grid?.items?.map((item, idx) => (
                    <div className="item" key={idx}>
                      <h3
                        className="title"
                        dangerouslySetInnerHTML={{ __html: item?.title! }}
                      />

                      <p
                        className="content"
                        dangerouslySetInnerHTML={{
                          __html: item?.content || '',
                        }}
                      />
                    </div>
                  ))}
                </div>
              </Col>

              {!!page?.section_6?.featured_quote?.text && (
                <Col xs={12} className="featured-quote">
                  <img
                    className="image"
                    src="/img/quote-image-alt.svg"
                    alt=""
                  />
                  <p className="text">
                    {page?.section_6?.featured_quote?.text}
                  </p>
                  <p className="author">
                    - {page?.section_6?.featured_quote?.author}
                  </p>
                </Col>
              )}

              {!!page?.section_6?.primary_button?.title && (
                <Col xs={12} className="buttons">
                  <Link
                    href={page?.section_6?.primary_button?.href!}
                    legacyBehavior
                    passHref
                  >
                    <Button mType="primary">
                      {page?.section_6?.primary_button?.title}
                    </Button>
                  </Link>
                </Col>
              )}
            </Row>
          </ResponsiveContainer>
        </Section6>
      )}
    </>
  );
};

export const VERTICAL_PAGE_QUERY = /**gql */ `
  all_vertical(where: { url: $url }) {
    items {
      title
      description
      url
      hero {
        eyebrow
        headline
        subheadline
        primary_button {
          title
          href
        }
        secondary_button {
          title
          href
        }
        imageConnection {
          edges {
            node {
              url
            }
          }
        }
      }
      section_1 {
        eyebrow
        headline
        subheadline
        imageConnection {
          edges {
            node {
              url
            }
          }
        }
      }
      section_2 {
        eyebrow
        headline
        subheadline
        primary_button {
          title
          href
        }
        accordion_items {
          title
          content
        }
      }
      section_3 {
        eyebrow
        headline
        primary_button {
          title
          href
        }
        secondary_button {
          title
          href
        }
        cards {
          title
          content
          iconConnection {
            edges {
              node {
                url
              }
            }
          }
        }
      }
      section_4 {
        eyebrow
        headline
        subheadline
        primary_button {
          title
          href
        }
        imageConnection {
          edges {
            node {
              url
            }
          }
        }
      }
      section_5 {
        eyebrow
        headline
        subheadline
        supporting_text
        cards {
          title
          content
          iconConnection {
            edges {
              node {
                url
              }
            }
          }
        }
      }
      section_6 {
        eyebrow
        headline
        subheadline
        featured_quote {
          author
          text
        }
        primary_button {
          title
          href
        }
        grid {
          title
          items {
            title
            content
          }
        }
      }
      footer {
        headline
        subheadline
        primary_button {
          title
          href
        }
        secondary_button {
          title
          href
        }
        third_button {
          title
          href
        }
      }
    }
  }
`;

const Hero = styled.section`
  background-size: cover;
  min-height: 760px;
  overflow: hidden;
  position: relative;
  max-width: 1600px;
  width: 100%;
  margin: 0 auto;

  .image {
    pointer-events: none;
    position: absolute;
    bottom: 0;
    right: 0;
    width: 50%;
    height: 100%;

    div,
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .fade {
    pointer-events: none;
    position: absolute;
    height: 100%;
    width: 40%;
    z-index: 2;
    left: 49%;
    background: linear-gradient(90deg, #181e2b 0%, #181e2b00 90%);
  }

  .content {
    position: relative;
    z-index: 3;
    padding-top: 180px;
    padding-bottom: 160px;

    ${HeroHeadline} {
      max-width: 700px;
    }

    ${HeroSubheadline} {
      max-width: 500px;
    }
  }

  .buttons {
    display: flex;
    gap: 24px;
  }

  ${mediaScreen('xs', 'md')} {
    min-height: auto;

    ${HeroHeadline}, ${HeroSubheadline} {
      max-width: 100%;
    }

    .fade {
      width: 70%;
    }

    .content {
      padding-top: 100px;
      padding-bottom: 60px;
    }

    .buttons {
      flex-direction: column;
      gap: 16px;

      .mButton {
        display: flex;
      }
    }
  }
`;

const Section1 = styled.section`
  overflow: hidden;

  .image {
    display: flex;
    justify-content: center;
    align-items: flex-end;

    img {
      margin-bottom: -10px;
      width: 120%;
      max-width: 120%;
    }
  }
  .texts {
    padding-top: 80px;
    padding-bottom: 90px;
  }

  ${mediaScreen('xs', 'md')} {
    .texts {
      padding-top: 00px;
      padding-bottom: 50px;
    }
  }
`;

const Section2 = styled.section`
  background: white;
  padding-top: 100px;
  padding-bottom: 100px;

  .texts {
    padding-right: 24px;
  }

  .accordion-wrapper {
    padding-left: 24px;
  }

  ${SectionEyebrow} {
    margin-top: 0;
  }

  ${SectionSubheadline} {
    margin-bottom: 32px;
  }

  ${mediaScreen('xs', 'md')} {
    padding-top: 40px;
    padding-bottom: 40px;

    .mButton {
      display: flex;
      width: 100%;
      margin-bottom: 40px;
    }

    .texts,
    .accordion-wrapper {
      padding: 0;
    }
  }
`;

const Section3 = styled(ResponsiveContainer)`
  padding-bottom: 90px;
  padding-top: 60px;
  position: relative;

  ${SectionHeadline} {
    max-width: 100%;
    margin-bottom: 40px;
  }

  .cards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    margin-bottom: 40px;
  }

  .card {
    background: ${(p) => p.theme.colors.slate2};
    border-radius: 8px;
    padding: 24px;
    position: relative;
    display: flex;
    flex-direction: column;
    text-align: left;
    overflow: hidden;

    .head {
      display: flex;
      align-items: center;
      margin-bottom: 24px;
    }

    .icon {
      width: 50px;
      height: 50px;
      margin-right: 16px;
    }

    .title {
      font-size: 24px;
      line-height: 1.35;
      color: ${(p) => p.theme.colors.white};
      font-weight: 700;
      margin: 0;

      b {
        font-weight: 700;
        color: ${(p) => p.theme.colors.teal};
      }
    }

    .content {
      font-size: 16px;
      line-height: 1.5;
      color: ${(p) => p.theme.colors.white};
      font-weight: 400;
      margin: 0;
    }
  }

  .buttons {
    display: flex;
    gap: 24px;
  }

  ${mediaScreen('xs', 'md')} {
    padding-bottom: 40px;
    padding-top: 24px;

    .cards {
      grid-template-columns: 1fr;
      gap: 24px;

      .title {
        font-size: 28px;
      }

      .content {
        font-size: 16px;
        line-height: 1.5;
      }
    }

    .buttons {
      flex-direction: column;
      gap: 24px;
      width: 100%;
      align-items: stretch;
      text-align: center;
    }
  }
`;

const Section4 = styled.section`
  background: white;
  padding-bottom: 100px;
  padding-top: 100px;
  max-width: 1600px;
  position: relative;

  ${SectionEyebrow} {
    margin-top: 0;
  }

  ${SectionSubheadline} {
    margin-bottom: 40px;
  }

  .angle {
    position: absolute;
    pointer-events: none;
    left: 0;
  }

  .image {
    img {
      position: relative;
      top: -5%;
      height: 110%;
    }
  }

  ${mediaScreen('xs', 'md')} {
    padding-bottom: 40px;
    padding-top: 40px;

    .image {
      margin-bottom: 40px;

      img {
        top: 0;
        left: -5%;
        height: auto;
        width: 110%;
        max-width: 110%;
      }
    }

    .mButton {
      width: 100%;
    }
  }
`;

const Section5 = styled(ResponsiveContainer)<{ columns: number }>`
  padding-bottom: 100px;
  padding-top: 100px;
  position: relative;

  ${SectionEyebrow} {
    margin-top: 0;
  }

  ${SectionEyebrow},${SectionHeadline}, ${SectionSubheadline} {
    width: 100%;
    text-align: center;
    max-width: 100%;
  }

  ${SectionSubheadline} {
    margin-bottom: 70px;
  }

  .cards {
    display: grid;
    grid-template-columns: repeat(${(p) => p.columns}, minmax(0, 1fr));
    justify-content: center;
    gap: 24px;
    margin-bottom: 70px;

    ${mediaScreen('md')} {
      & > * {
        width: 100%;
      }
      & > :nth-child(4n + 1):nth-last-child(-n + 3) {
        grid-column: 2;
      }
      & > :last-child:nth-child(4n + 1) {
        grid-column: 2 / span 2;
      }
      & > :nth-child(4n + 1):nth-last-child(2) {
        grid-column: 2;
      }
      & > :nth-child(4n + 1):nth-last-child(2) + * {
        grid-column: 3;
      }
      & > :nth-child(4n + 1):nth-last-child(3) {
        grid-column: 1;
      }
      & > :nth-child(4n + 1):nth-last-child(3) + * {
        grid-column: 2;
      }
      & > :nth-child(4n + 1):nth-last-child(3) + * + * {
        grid-column: 3 / span 2;
      }
    }
  }

  .card {
    background: ${(p) => p.theme.colors.slate2};
    border-radius: 8px;
    padding: 24px;
    position: relative;
    display: flex;
    flex-direction: column;
    text-align: left;
    overflow: hidden;

    .icon {
      width: 50px;
      height: 50px;
      margin-bottom: 16px;
    }

    .title {
      font-size: 22px;
      line-height: 1.35;
      color: ${(p) => p.theme.colors.white};
      font-weight: 500;
      margin: 0;
      text-transform: uppercase;
      margin-bottom: 4px;

      sub,
      sup {
        vertical-align: baseline;
      }

      sub {
        letter-spacing: 3px;
        font-size: 19px;
      }

      sup {
        font-weight: 400;
        font-size: 40px;
        b {
          font-weight: 400;
        }
      }

      b {
        font-weight: 500;
        color: ${(p) => p.theme.colors.teal};
      }
    }

    .content {
      font-size: 16px;
      line-height: 1.5;
      color: ${(p) => p.theme.colors.white};
      font-weight: 400;
      margin: 0;
    }
  }

  .supporting-text {
    width: 100%;
    max-width: 700px;
    margin: 0 auto;
    text-align: center;
    font-size: 20px;
    font-weight: 400;
    line-height: 1.5;

    b,
    strong {
      color: ${(p) => p.theme.colors.yellow};
    }
  }

  ${mediaScreen('xs', 'md')} {
    padding-bottom: 40px;
    padding-top: 40px;

    ${SectionEyebrow},
    ${SectionHeadline}, 
    ${SectionSubheadline}, 
    .supporting-text {
      text-align: left;
    }

    ${SectionSubheadline} {
      margin-bottom: 40px;
    }

    .cards {
      grid-template-columns: repeat(2, 1fr);
      gap: 16px;
      margin-bottom: 40px;

      .card {
        padding: 24px 16px;

        &:nth-child(odd):last-child {
          grid-column: 1 / -1;
        }

        .icon,
        .spacing {
          display: none;
        }

        .title {
          font-size: 16px;
          margin-bottom: 16px;

          sub {
            letter-spacing: 2px;
            font-size: 16px;
          }

          sup {
            font-size: 28px;
          }
        }

        .content {
          font-size: 16px;
          line-height: 1.5;
        }
      }
    }
  }
`;

const Section6 = styled.section<{ columns: number }>`
  padding-top: 100px;
  padding-bottom: 100px;
  background-color: white;
  background-image: url('/img/dots-bg-alt.svg');
  background-repeat: no-repeat;
  background-position: bottom right;
  background-size: 100%;

  .texts {
    > * {
      max-width: 450px;
    }
  }

  .grid-title {
    width: 100%;
    font-size: 48px;
    color: ${(p) => p.theme.colors.eb1};
    margin: 0;
    text-align: center;
    border-bottom: 1px solid ${(p) => p.theme.colors.eb3};
    padding: 32px 24px;
    font-weight: 400;
  }

  .grid {
    display: grid;
    gap: 0;
    grid-template-columns: repeat(${(p) => p.columns}, minmax(0, 1fr));

    .item {
      padding: 32px 24px;
      border-top: 1px solid ${(p) => p.theme.colors.eb3};
      border-left: 1px solid ${(p) => p.theme.colors.eb3};

      &:nth-child(-n + ${(p) => p.columns}) {
        border-top: 0;
      }

      &:nth-child(${(p) => p.columns}n + 1) {
        border-left: 0;
      }

      .title {
        color: ${(p) => p.theme.colors.eb1};
        font-size: 40px;
        line-height: 1.3;
        font-weight: 400;
        margin: 0;
      }

      .content {
        color: ${(p) => p.theme.colors.slate1};
        font-size: 16px;
        line-height: 1.5;
        margin-top: 16px;
      }
    }
  }

  .featured-quote {
    position: relative;
    margin-top: 80px;

    .image {
      position: absolute;
      top: 0;
      left: 0;
      width: 110px;
      opacity: 0.6;
    }

    .text {
      font-size: 40px;
      line-height: 1.5;
      margin: 0;
      color: ${(p) => p.theme.colors.slate3};
      width: 100%;
      padding-left: 80px;
      padding-top: 35px;
    }

    .author {
      font-size: 20px;
      line-height: 1.5;
      margin: 16px 0 0 0;
      color: ${(p) => p.theme.colors.teal};
      width: 100%;
      text-align: right;
    }
  }

  .buttons {
    text-align: center;
    margin-top: 40px;
  }

  ${mediaScreen('xs', 'md')} {
    padding-top: 20px;
    padding-bottom: 40px;

    ${SectionSubheadline} {
      margin-bottom: 32px;
    }

    .grid-title {
      font-size: 36px;
      padding: 24px 16px;
    }

    .grid {
      grid-template-columns: repeat(1, minmax(0, 1fr));

      .item {
        padding: 24px 16px;
        border: 0;
        border-bottom: 1px solid ${(p) => p.theme.colors.eb3};

        &:last-child {
          border-bottom: 0;
        }

        .title {
          font-size: 28px;
          text-align: center;
        }

        .content {
          text-align: center;
          margin-top: 8px;
          margin-bottom: 0;
        }
      }
    }

    .featured-quote {
      margin-top: 40px;

      .image {
        width: 64px;
      }

      .text {
        font-size: 24px;
        padding-left: 45px;
        padding-top: 20px;
      }

      .author {
        font-size: 16px;
      }
    }

    .buttons {
      .mButton {
        width: 100%;
      }
    }
  }
`;
